import {
  InteractionType,
  LogLevel,
  PublicClientApplication,
  type IPublicClientApplication,
} from '@azure/msal-browser';

import { getAuthConfig } from '../../config/runtime-config';

import type { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';

/**
 * MSAL wiring for the Photo QC app.
 *
 * Mirrors the Creator frontend deliberately: the two apps share one Entra app
 * registration, so a user who signed into Creator reaches QC without seeing a
 * login screen — Entra recognises its own session cookie and redirects straight
 * back. Diverging here would break that.
 */

export function loggerCallback(_logLevel: LogLevel, _message: string): void {}

export function MSALInstanceFactory(): IPublicClientApplication {
  const config = getAuthConfig();

  // Derived from the live origin so localhost stays on localhost and each
  // deployed host uses its own. Note the trailing slash: Entra matches redirect
  // URIs as exact strings, so `https://photo-qc.davaco.ai/` must be registered
  // with it. Registering the slashless form yields AADSTS50011, which reads
  // like a code fault and is not one.
  const origin = window.location.origin;

  return new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      redirectUri: `${origin}/`,
      postLogoutRedirectUri: `${origin}/`,
      // NOTE: navigateToLoginRequestUrl is deliberately absent. In msal-browser
      // v5 it is no longer an auth config option — it moved to a per-call option
      // on handleRedirectPromise, where it defaults to true:
      //
      //   const navigateToLoginRequestUrl = options?.navigateToLoginRequestUrl ?? true;
      //
      // initializeMsal calls handleRedirectPromise() with no options, so that
      // default applies. This is what carries a deep link across the sign-in
      // round trip: a user arriving at /analysis-report-questions?templateId=…
      // is returned to that exact URL, query string intact, rather than dumped
      // on the app root. Pass options there to change it, not here.
    },
    cache: {
      cacheLocation: 'localStorage',
      // storeAuthStateInCookie was removed in v5; CacheOptions now takes only
      // cacheLocation and cacheRetentionDays.
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    // Redirect, never popup or a silent iframe. A hidden iframe to
    // login.microsoftonline.com sends the Entra session cookie as a
    // third-party cookie, which Safari and Firefox block by default — SSO then
    // fails for some users and not others. A top-level redirect is first-party
    // and always carries the cookie.
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: getAuthConfig().apiScopes,
    },
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  // Left empty on purpose. AuthTokenInterceptor attaches tokens to this app's
  // own API, and it needs to pick the API URL up at runtime (window.__env),
  // which a static protectedResourceMap built at bootstrap cannot express.
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map<string, Array<string>>(),
  };
}
